import { BillingPlans } from '../../../dashboard/pages/SettingsPage/BillingPanel2023/BillingPlans/BillingPlans';
import React, { useEffect, useMemo, useState } from 'react';
import { useShopifyBillingPlans } from '../../../dashboard/pages/SettingsPage/BillingPanel2023/BillingPlans/use-shopify-billing-plans';
import css from './ShopifyBilling.module.css';
import { Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectIsShopifyHasAnyPurchase,
  selectShopifyShop,
} from '@mm/state/selectors/viewSelectors';
import { useFreeTrialUpgradeModal } from '../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ReactComponent as LeadMagnet } from 'assets/lead-generation-magnet.svg';

import moment from 'moment/moment';
import classNames from 'classnames';
import { Spinner } from '@mm/components';
import ShopifyController from '@mm/state/controllers/ShopifyController';
import { ShopifyShop } from '@mm/state/interfaces/shopify';
import { FreeTrialCountdown } from '../../../layout/BotBuilderSidebar/FreeTrialCountdown/FreeTrialCountdown';

export const ShopifyBilling = () => {
  const shopifyBillingPlans = useShopifyBillingPlans({ withFree: true });
  const isShopifyWithAnyPurchase = useSelector(selectIsShopifyHasAnyPurchase);
  const sessionShop = useSelector(selectShopifyShop);

  const [busy, setBusy] = useState(false);
  const [shopData, setShopData] = useState<ShopifyShop>();

  const handleBusy = () => setBusy(false);
  const handleUnbusy = () => setBusy(false);

  const freeTrialUpgradeModal = useFreeTrialUpgradeModal();

  const onBillingSuccess = () => {};
  const history = useHistory();

  const leadsCapturedCount = shopData?.contactsCount;
  const totalLeadsAllowed = shopData?.contactsLimits;
  const totalLeadsCharged = shopData?.chargedContactsCount;

  const isCurrentSubscriptionActive =
    freeTrialUpgradeModal.currentSubscription?.state === 'active';

  const chipStyle = useMemo(() => {
    switch (freeTrialUpgradeModal.currentSubscription?.planCode) {
      case 'shopify_starter':
        return css.blue;
      case 'shopify_pro':
        return css.cyan;
      case 'shopify_advanced':
        return css.purple;
      default:
        return css.green;
    }
  }, [
    freeTrialUpgradeModal.currentSubscription?.planCode,
    isCurrentSubscriptionActive,
  ]);

  const currentPlanName = isCurrentSubscriptionActive
    ? `${
        shopifyBillingPlans.plans.find(
          p =>
            p.planCode === freeTrialUpgradeModal.currentSubscription?.planCode,
        )?.title || ''
      }`
    : 'Free';

  const fetchShop = async () => {
    if (sessionShop?.id) {
      try {
        handleBusy();
        const res = await ShopifyController.getShop(sessionShop?.id);
        setShopData(res);
      } finally {
        handleUnbusy();
      }
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('useFreeTrialUpgradeModal:', freeTrialUpgradeModal);
  }, [freeTrialUpgradeModal]);

  useEffect(() => {
    fetchShop();
  }, []);

  return (
    <div className={css.page}>
      <FreeTrialCountdown choosePlanVisible={false} />

      <div className={css.header}>
        <Button onClick={() => history.push('/')} type="text">
          {`<`} Back
        </Button>
      </div>

      <Spinner
        show={
          shopifyBillingPlans.isPurchasing ||
          shopifyBillingPlans.isCancelling ||
          busy
        }
        backgroundColor="rgba(0,0,0,0.05)"
      />

      <div className={css.content}>
        <div className={css.info}>
          <span className={css.name}>
            {sessionShop?.shopifyDomain && (
              <span>Store: {sessionShop.shopifyDomain}</span>
            )}

            {currentPlanName && (
              <span
                className={classNames(css.chip, {
                  [chipStyle]: true,
                })}
              >
                {currentPlanName}
              </span>
            )}
          </span>

          <span className={css.createdAt}>
            {sessionShop?.createdAt
              ? `Created: ${moment(sessionShop.createdAt).format('MM/DD/YYYY')}`
              : ''}
          </span>

          {currentPlanName && (
            <Button
              onClick={shopifyBillingPlans.handleCancel}
              className={css.cancelBtn}
            >
              Cancel current plan
            </Button>
          )}
        </div>

        <div className={css.stats}>
          <div className={css.table}>
            <div className={css.tableHeader}>
              <span className={css.tableHeaderTitle}>Service</span>
              <span className={css.tableHeaderTitle}>Captured</span>

              {isShopifyWithAnyPurchase ? (
                <span className={css.tableHeaderTitle}>Charged</span>
              ) : (
                <span className={css.tableHeaderTitle}>Limit</span>
              )}
            </div>

            <div className={css.row}>
              <span className={classNames(css.value, css.bold)}>
                <LeadMagnet className={css.magnetIcon} />
                X-Ray Leads
              </span>
              <span className={css.value}>{leadsCapturedCount}</span>
              {isShopifyWithAnyPurchase ? (
                <span className={css.value}>{totalLeadsCharged}</span>
              ) : (
                <span className={css.value}>{totalLeadsAllowed}</span>
              )}
            </div>
          </div>
        </div>

        <div className={css.card}>
          <h1 className={css.title}>Choose plan</h1>
          <BillingPlans
            className={css.plans}
            whatRowAlwaysVisible
            costPerContactRowAlwaysVisible
            plans={shopifyBillingPlans.plans}
            onBillingSuccess={onBillingSuccess}
            hideFreePlan={false}
            hideMonthlyManagementFee
          />
        </div>
      </div>
    </div>
  );
};
